import React, { useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer, LineChart, Line,
} from 'recharts';
import { AppLayout } from '../components/layout/AppLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { StatCard, Card, Skeleton, Badge } from '../components/common';
import { analyticsAPI, emailsAPI } from '../services/api';
import { useAsync, useMediaQuery } from '../hooks';
import { timeAgo, statusColor, pct } from '../utils/helpers';
import { useNavigate } from 'react-router-dom';

const CHART_COLORS = { grid: '#1e1e2e', cyan: '#00e1c8', amber: '#f5a623', green: '#3dffa0' };

const CustomTooltipStyle = {
  background: 'var(--bg-elevated)',
  border: '1px solid var(--border-light)',
  borderRadius: 8,
  fontSize: '0.75rem',
  fontFamily: 'var(--font-mono)',
  color: 'var(--text-primary)',
  padding: '8px 12px',
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={CustomTooltipStyle}>
      <p style={{ color: 'var(--text-muted)', marginBottom: 4 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  );
};

const RecentEmailRow = ({ email, onClick }) => (
  <tr onClick={() => onClick(email._id)} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 120ms' }}
    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
    onMouseLeave={(e) => e.currentTarget.style.background = ''}>
    <td style={{ padding: '12px 16px', maxWidth: 220 }}>
      <p style={{ fontSize: '0.82rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email.subject}</p>
      <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{email.recipientEmail}</p>
    </td>
    <td style={{ padding: '12px 16px' }}>
      <span style={{ fontSize: '0.78rem', fontFamily: 'var(--font-mono)', color: statusColor(email.status) }}>{email.status}</span>
    </td>
    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
      <span style={{ fontSize: '0.82rem', fontFamily: 'var(--font-mono)', color: email.openCount > 0 ? 'var(--cyan)' : 'var(--text-muted)' }}>{email.openCount}</span>
    </td>
    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
      <span style={{ fontSize: '0.82rem', fontFamily: 'var(--font-mono)', color: email.clickCount > 0 ? 'var(--amber)' : 'var(--text-muted)' }}>{email.clickCount}</span>
    </td>
    <td style={{ padding: '12px 16px' }}>
      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{timeAgo(email.createdAt)}</span>
    </td>
  </tr>
);

export const DashboardPage = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const fetchSummary = useCallback(() => analyticsAPI.summary().then((r) => r.data.data), []);
  const fetchHours   = useCallback(() => analyticsAPI.opensByHour().then((r) => r.data.data), []);
  const fetchEmails  = useCallback(() => emailsAPI.list({ limit: 8 }).then((r) => r.data.data), []);

  const { data: summary, loading: sLoading } = useAsync(fetchSummary);
  const { data: hours,   loading: hLoading } = useAsync(fetchHours);
  const { data: emailsData, loading: eLoading } = useAsync(fetchEmails);

  // Build 24-hour chart data
  const hourlyData = Array.from({ length: 24 }, (_, h) => {
    const found = hours?.find((d) => d._id?.hour === h);
    return { hour: `${String(h).padStart(2, '0')}:00`, opens: found?.count || 0 };
  });

  return (
    <AppLayout>
      <PageHeader
        title="Dashboard"
        subtitle="Real-time overview of your email tracking"
        actions={
          <button onClick={() => navigate('/compose')} style={{
            padding: '8px 18px', borderRadius: 'var(--radius-md)',
            background: 'var(--cyan)', color: '#0a0a0f',
            fontFamily: 'var(--font-mono)', fontSize: '0.78rem', fontWeight: 700,
            letterSpacing: '0.05em', cursor: 'pointer', border: 'none',
            transition: 'all 150ms',
          }}>+ Compose</button>
        }
      />

      <div style={{ padding: isMobile ? '16px' : '24px 32px', display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: 16 }}>
          {sLoading ? (
            Array(4).fill(0).map((_, i) => (
              <Card key={i} style={{ padding: '20px 24px' }}>
                <Skeleton height={12} width={80} style={{ marginBottom: 12 }} />
                <Skeleton height={32} width={60} />
              </Card>
            ))
          ) : (
            <>
              <StatCard label="Emails Sent"   value={summary?.totalEmails ?? 0}    icon="✉"  accent="var(--text-primary)" />
              <StatCard label="Total Opens"   value={summary?.totalOpens ?? 0}     icon="👁"  accent="var(--cyan)" />
              <StatCard label="Total Clicks"  value={summary?.totalClicks ?? 0}    icon="🔗"  accent="var(--amber)" />
              <StatCard label="Open Rate"     value={summary ? pct(summary.totalOpens, summary.totalEmails) : '—'} icon="📊"  accent="var(--green)" sub={`${summary?.uniqueRecipients ?? 0} unique recipients`} />
            </>
          )}
        </div>

        {/* Charts Row */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: 16 }}>
          {/* Hourly Opens */}
          <Card style={{ padding: '20px 24px' }}>
            <p style={{ fontSize: '0.72rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 16 }}>Opens by Hour (Last 30 Days)</p>
            {hLoading ? <Skeleton height={180} /> : (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={hourlyData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                  <defs>
                    <linearGradient id="cyanGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00e1c8" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#00e1c8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fill: '#4a4a65', fontSize: 10, fontFamily: 'Space Mono' }} tickLine={false} axisLine={false} interval={3} />
                  <YAxis tick={{ fill: '#4a4a65', fontSize: 10, fontFamily: 'Space Mono' }} tickLine={false} axisLine={false} />
                  <ReTooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="opens" stroke="#00e1c8" fill="url(#cyanGrad)" strokeWidth={2} dot={false} name="Opens" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Engagement Summary */}
          <Card style={{ padding: '20px 24px' }}>
            <p style={{ fontSize: '0.72rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 16 }}>Engagement</p>
            {sLoading ? <Skeleton height={180} /> : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart
                  data={[
                    { name: 'Sent',    value: summary?.totalEmails ?? 0,  fill: '#4a4a65' },
                    { name: 'Opened',  value: summary?.totalOpens  ?? 0,  fill: '#00e1c8' },
                    { name: 'Clicked', value: summary?.totalClicks ?? 0,  fill: '#f5a623' },
                  ]}
                  margin={{ top: 0, right: 0, bottom: 0, left: -20 }}
                >
                  <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: '#4a4a65', fontSize: 10, fontFamily: 'Space Mono' }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: '#4a4a65', fontSize: 10, fontFamily: 'Space Mono' }} tickLine={false} axisLine={false} />
                  <ReTooltip content={<ChartTooltip />} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} name="Count">
                    {[0, 1, 2].map((i) => (
                      <rect key={i} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>

        {/* Recent Emails Table */}
        <Card style={{ overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontSize: '0.72rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Recent Emails</p>
            <button onClick={() => navigate('/emails')} style={{ fontSize: '0.72rem', color: 'var(--cyan)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-mono)' }}>View all →</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Subject / Recipient', 'Status', 'Opens', 'Clicks', 'Sent'].map((h) => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: h === 'Opens' || h === 'Clicks' ? 'center' : 'left', fontSize: '0.68rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {eLoading ? (
                  Array(5).fill(0).map((_, i) => (
                    <tr key={i}><td colSpan={5} style={{ padding: '12px 16px' }}><Skeleton height={14} /></td></tr>
                  ))
                ) : emailsData?.emails?.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>No emails sent yet</td></tr>
                ) : (
                  emailsData?.emails?.map((email) => (
                    <RecentEmailRow key={email._id} email={email} onClick={(id) => navigate(`/emails/${id}`)} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AppLayout>
  );
};
